import "./SendSurveyButton.scss";

import { Button, ButtonProps, Flex, Tooltip } from "@fluentui/react-northstar";
import { app } from "@microsoft/teams-js";
import { TeamsSurveyState } from "@teams/types";
import { SurveyDTO } from "@teams/types/api";
import React, { useMemo, useState } from "react";

import { useUserInfo } from "../../../../_shared/actions/useUserInfo";
import { PollyIcon } from "../../../../_shared/components/PollyIcon";
import { FetchError, isError } from "../../../../_shared/services/ChannelApi";
import { surveyApi } from "../../../../_shared/services/SurveyApi";
import { userCanCreate } from "../../../../_shared/utils/userHelpers";

type Props = {
  survey: SurveyDTO;
  teamsContext: app.Context;
  onChanged: (newSurveyState?: TeamsSurveyState) => void;
  onError?: (error: string) => void;
} & Pick<ButtonProps, "size">;

export const SendSurveyButton: React.FC<Props> = ({
  survey,
  teamsContext,
  onChanged,
  onError,
  size,
}) => {
  const [isSending, setIsSending] = useState<boolean>();
  const { user } = useUserInfo();

  const canCreate = useMemo(() => userCanCreate(user), [user]);

  const getButtonContent = () => {
    if (survey.type === "qna") {
      return isSending ? "Launching..." : "Start Q&A";
    }
    return isSending ? "Sending..." : "Send";
  };

  const getTooltipContent = () => {
    if (survey.type === "qna") {
      if (canCreate) {
        return `Open the Q&A session now`;
      }
      return "Launching is disabled due to monthly response limit being exceeded";
    }

    if (canCreate) {
      return `Send the ${survey.type === "quiz" ? "quiz" : "polly"} now`;
    }
    return "Monthly response limit exceeded!  Upgrade plan to send this polly!";
  };

  return (
    <Tooltip
      content={getTooltipContent()}
      trigger={
        <Flex styles={{ flex: 1 }}>
          <Button
            primary
            className="send-survey-button"
            fluid
            loading={isSending}
            disabled={!canCreate}
            size={size}
            icon={<PollyIcon icon="send-icon" inheritColor />}
            onClick={async () => {
              if (isSending || !canCreate) {
                return;
              }

              setIsSending(true);
              const result = await surveyApi.deliverSurvey(teamsContext, survey.id as string);

              if (isError(result)) {
                setIsSending(false);
                const message =
                  result instanceof FetchError ? (result.body?.message as string) : result.message;
                onError && onError(message);
                console.error(result);
                return;
              }
              onChanged("open");
            }}
            content={getButtonContent()}
          />
        </Flex>
      }
    />
  );
};
